/**
 * THE CONVERSATION AS A MESSENGER SHOWS IT, for the chat box and the mind's
 * thread alike: consecutive messages from one side grouped (the first and
 * last of each group marked, for the bubbles' corners and gaps), system
 * events between them, and a time line where the conversation picks up again
 * after a while. Lines the page sent (`context`) are not part of it.
 */
import type { CogentMessage } from "./chat-view";
type ThreadItem =
  | {
      kind: "time";
      key: string;
      label: string;
    }
  | {
      kind: "event";
      key: string;
      text: string;
    }
  | {
      kind: "message";
      key: string;
      message: CogentMessage;
      first: boolean;
      last: boolean;
    };
/** A pause this long between two messages shows the time again. */
const pauseMs = 30 * 60e3;
/** "10:42" today; "Mon 10:42" this week; "Oct 3, 10:42" before. */
export function clock({
  at,
  now = Date.now(),
}: {
  readonly at: string;
  readonly now?: number;
}): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const today = new Date(now);

  if (d.toDateString() === today.toDateString()) return time;

  if (now - d.getTime() < 6 * 86400e3)
    return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;

  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}
export function threadOf(messages: readonly CogentMessage[]): ThreadItem[] {
  const shown = messages.filter((m) => !m.context);
  const out: ThreadItem[] = [];
  let lastAt: number | null = null;

  for (const m of shown) {
    if (m.at) {
      const at = Date.parse(m.at);

      if (lastAt === null || at - lastAt > pauseMs)
        out.push({
          kind: "time",
          key: `time:${m.id}`,
          label: clock({ at: m.at }),
        });

      lastAt = at;
    }

    appendMessage({ thread: out, message: m });
  }

  return out;
}

function appendMessage({
  thread,
  message,
}: Readonly<{
  thread: ThreadItem[];
  message: CogentMessage;
}>): void {
  if (message.from === "event") {
    thread.push({ kind: "event", key: message.id, text: message.text });
    return;
  }

  const previous = thread.at(-1);
  const continues =
    previous?.kind === "message" && previous.message.from === message.from;

  if (continues) previous.last = false;

  thread.push({
    kind: "message",
    key: message.id,
    message,
    first: !continues,
    last: true,
  });
}
